import { Link } from 'react-router-dom';
import {
  CalendarDays, CreditCard, Package, Tag, TrendingUp, Users, Wrench, Zap,
} from 'lucide-react';
import { usePageMeta, Reveal, Count, useScrollSpy } from '../lib/site';
import { CardHeader, CtaBand } from '../components/Bits';
import DemoPanel from '../components/DemoPanel';
import TeeSheetFrame from '../components/TeeSheetFrame';

const SECTIONS = ['demand', 'ops', 'demo', 'plan'];

const OPS = [
  {
    icon: CalendarDays, title: 'Tee sheet',
    body: 'The day at a glance: bookings, walk-ins, blocks, and who is standing on the first tee.',
  },
  {
    icon: Tag, title: 'Rate engine',
    body: 'Rack rate and app rate, set by you, applied everywhere a golfer sees your course.',
  },
  {
    icon: TrendingUp, title: 'Dynamic pricing',
    body: 'Automated inside a floor and ceiling you set. Weather, sheet fill, and time to tee-off move the price. It never leaves your band.',
  },
  {
    icon: Zap, title: 'Flash Deals',
    body: 'Course-initiated, time-boxed discounts pushed to golfers in the app. You fire them, you end them. Nothing runs on its own.',
  },
  {
    icon: CreditCard, title: 'Point of sale',
    body: 'An in-house register built for a golf counter: split the bill, take pay-from-phone, keep the line moving.',
  },
  {
    icon: Users, title: 'Staff scheduling',
    body: 'Shifts and assignments for the crew, from the pro shop to the mowers, without a whiteboard.',
  },
  {
    icon: Wrench, title: 'Issue tracking',
    body: 'Log the broken sprinkler head from the cart, assign it, and see it fixed. Maintenance stops living in text messages.',
  },
  {
    icon: Package, title: 'Supplier ordering',
    body: 'Catalogs, cart, order history, and stock visibility across pro shop and F&B vendors. It is straightforward procurement; nothing reorders itself.',
  },
];

export default function Courses() {
  usePageMeta(
    'For courses | MyGolfLinks',
    'LinksOS is the tee sheet with demand built in: local golfers book straight into your sheet from the MyGolfLinks app, with pricing you control, Flash Deals, POS, and the rest of the back office.'
  );
  const active = useScrollSpy(SECTIONS);

  return (
    <main>
      {/* ── Hero ── */}
      <section style={{ padding: 'clamp(140px, 16vw, 180px) 0 clamp(48px, 6vw, 70px)' }}>
        <div className="wrap">
          <Reveal>
            <p className="mono" style={{ fontSize: 12.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', fontWeight: 500, marginBottom: 20 }}>
              For courses
            </p>
            <h1 className="display" style={{ maxWidth: 780 }}>The tee sheet with demand built in.</h1>
            <p className="lede" style={{ margin: '24px 0 32px' }}>
              LinksOS is tee sheet software. The difference is what comes attached: the MyGolfLinks
              player app, where local golfers find your course and book straight into your sheet.
              You implement one platform, and the demand shows up with it.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link to="/demo" className="btn btn-gold">Book a demo</Link>
              <a href="#demo" className="btn btn-line">Skip to the live sandbox</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Sticky section nav ── */}
      <nav className="subnav" aria-label="Page sections">
        <div className="subnav-in">
          {[
            ['demand', 'Demand'],
            ['ops', 'Operations'],
            ['demo', 'Live sandbox'],
            ['plan', 'The plan'],
          ].map(([id, label]) => (
            <a key={id} href={`#${id}`} className={active === id ? 'active' : undefined}>
              {label}
            </a>
          ))}
        </div>
      </nav>

      {/* ── Demand ── */}
      <section id="demand">
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="01" label="The demand side" />
            <h2 className="h2" style={{ maxWidth: 660 }}>More golfers, straight into your sheet</h2>
          </Reveal>
          <Reveal className="ledger" style={{ marginTop: 40 }}>
            <div className="led">
              <span className="led-no">1</span>
              <span className="led-title">Golfers browse, you get found</span>
              <span className="led-body">
                MyGolfLinks players see courses near them with real rates and real availability.
                Being on the sheet is part of the plan, not a listing fee.
              </span>
            </div>
            <div className="led">
              <span className="led-no">2</span>
              <span className="led-title">Bookings at your price</span>
              <span className="led-body">
                Your rack rate and your app rate, set by you. No parity clause stops you from
                pricing your own course however you want, anywhere.
              </span>
            </div>
            <div className="led">
              <span className="led-no">3</span>
              <span className="led-title">Deals reach real golfers</span>
              <span className="led-body">
                When you fire a Flash Deal, it pushes to app golfers near your course. That is the
                other half of the loop: the demand side already exists when you need to move
                inventory.
              </span>
            </div>
            <div className="led">
              <span className="led-no">4</span>
              <span className="led-title">Every booking has a name on it</span>
              <span className="led-body">
                The golfer's contact lands in your book, not a marketplace's database. Next month
                you can reach them yourself, free.
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Operations ── */}
      <section id="ops" className="band-card">
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="02" label="The back office" />
            <h2 className="h2" style={{ maxWidth: 660 }}>One platform runs the whole shop</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              LinksOS replaces the tee sheet software you run today, and the rest of the operation
              comes with it in the same plan. No modules, no upsells. Switching platforms is a real
              decision, which is why the demo starts with the reason to move: the audience that
              comes attached.
            </p>
          </Reveal>
          <Reveal style={{ marginTop: 40, maxWidth: 860 }} d={1}>
            <TeeSheetFrame />
          </Reveal>
          <Reveal className="ops" style={{ marginTop: 48 }} d={1}>
            {OPS.map((op) => {
              const Icon = op.icon;
              return (
                <div className="op" key={op.title}>
                  <span className="op-ic"><Icon size={19} aria-hidden="true" /></span>
                  <span>
                    <h4>{op.title}</h4>
                    <p>{op.body}</p>
                  </span>
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* ── Live sandbox ── */}
      <section id="demo" className="band-pine on-pine">
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="03" label="Live sandbox" dark />
            <h2 className="h2" style={{ maxWidth: 660 }}>Click around the real thing</h2>
            <p className="lede" style={{ margin: '16px 0 0' }}>
              A working sandbox of LinksOS with sample data. Book a seat on the sheet. Drag the
              pricing band and watch the day reprice inside it. Fire a Flash Deal and watch it land
              on the phone. Not a screenshot, not a video.
            </p>
          </Reveal>
          <Reveal style={{ marginTop: 40 }} d={1}>
            <DemoPanel />
          </Reveal>
        </div>
      </section>

      {/* ── The plan ── */}
      <section id="plan">
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="04" label="The plan" />
            <h2 className="h2" style={{ maxWidth: 620 }}>One plan runs all of it</h2>
          </Reveal>
          <Reveal className="g2" style={{ marginTop: 40, alignItems: 'start' }} d={1}>
            <div>
              <div style={{ display: 'flex', gap: 34, flexWrap: 'wrap' }}>
                <div>
                  <div className="mono" style={{ fontSize: 'clamp(2.2rem, 4vw, 3rem)', fontWeight: 600 }}>
                    $<Count value={197} />
                  </div>
                  <div style={{ fontSize: '0.88rem', color: 'var(--mut)' }}>LinksOS, smaller courses</div>
                </div>
                <div>
                  <div className="mono" style={{ fontSize: 'clamp(2.2rem, 4vw, 3rem)', fontWeight: 600 }}>
                    $<Count value={495} />
                  </div>
                  <div style={{ fontSize: '0.88rem', color: 'var(--mut)' }}>LinksOS+, mid-size and large</div>
                </div>
              </div>
              <p style={{ color: 'var(--mut)', marginTop: 18, maxWidth: '52ch' }}>
                Everything included at both sizes. On top of the plan, small transaction fees, so we
                only earn more when your course earns more.
              </p>
            </div>
            <div className="ledger">
              <div className="led" style={{ gridTemplateColumns: '150px 1fr' }}>
                <span className="mono" style={{ fontWeight: 600 }}>$1.80 flat</span>
                <span className="led-body">
                  booking fee per player booked through the app, shown upfront. Your own website
                  carries no MyGolfLinks fee.
                </span>
              </div>
              <div className="led" style={{ gridTemplateColumns: '150px 1fr' }}>
                <span className="mono" style={{ fontWeight: 600 }}>$0</span>
                <span className="led-body">
                  to you on supplier orders. The supplier pays the platform fee, not your invoice.
                </span>
              </div>
              <div className="led" style={{ gridTemplateColumns: '150px 1fr' }}>
                <span className="mono" style={{ fontWeight: 600 }}>at launch</span>
                <span className="led-body">
                  per-transaction fees on Flash Deals and in-house POS sales, finalized and in
                  writing before you pay one
                </span>
              </div>
            </div>
          </Reveal>
          <Reveal style={{ marginTop: 26 }} d={2}>
            <Link to="/pricing" className="tlink">See the full pricing page</Link>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Bring it to your course"
        sub="Thirty minutes on a video call. We start with how golfers find you, then walk whatever parts of LinksOS you want to see."
      />
    </main>
  );
}
